import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { academicApi } from '@/services/academic.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';
import { PersonTabPanel, PersonTabs } from '@/modules/people/person-detail-tabs';

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/** "Auto-Matrícula" + "Historial de Calificaciones" — what a signed-in
 *  member sees about their own academic life, across every school. */
export function MyAcademicPage() {
  const { t } = useI18n();
  const [tab, setTab] = useState('available');

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('academic.my.title')}</h1>
        </div>
      </header>

      <PersonTabs
        label={t('academic.my.title')}
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'available', label: t('academic.my.available') },
          { id: 'transcript', label: t('academic.my.transcript') },
        ]}
      />

      <PersonTabPanel id="available" active={tab}>
        <AvailableSectionsTab />
      </PersonTabPanel>
      <PersonTabPanel id="transcript" active={tab}>
        <MyTranscriptTab />
      </PersonTabPanel>
    </div>
  );
}

function AvailableSectionsTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const sections = useQuery({
    queryKey: ['academic', 'course-sections', 'open'],
    queryFn: ({ signal }) =>
      academicApi.listCourseSections(
        { hasOpenCapacity: true, includeArchived: false, limit: 100 },
        signal,
      ),
  });

  const enrollSelf = useMutation({
    mutationFn: (sectionId: string) => academicApi.enrollSelf(sectionId),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['academic', 'course-sections', 'open'] });
      void queryClient.invalidateQueries({ queryKey: ['academic', 'my', 'transcript'] });
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const list = sections.data?.data ?? [];

  return (
    <div className="settings-card">
      {error && <Alert tone="danger">{error}</Alert>}
      {sections.isPending && <Spinner label={t('common.states.loading')} />}
      {sections.isSuccess && list.length === 0 && (
        <EmptyState title={t('academic.my.noneAvailable')} />
      )}

      {list.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('academic.subject.title')}</th>
                <th scope="col">{t('academic.courseSection.term')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {list.map((section) => (
                <tr key={section.id}>
                  <td>{section.subjectName}</td>
                  <td dir="ltr">
                    {section.term}
                    {section.schedule && ` · ${section.schedule}`}
                  </td>
                  <td>
                    <Button
                      variant="primary"
                      loading={enrollSelf.isPending && enrollSelf.variables === section.id}
                      onClick={() => enrollSelf.mutate(section.id)}
                    >
                      {t('academic.my.enrollMe')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MyTranscriptTab() {
  const { t } = useI18n();
  const transcript = useQuery({
    queryKey: ['academic', 'my', 'transcript'],
    queryFn: ({ signal }) => academicApi.myTranscript(signal),
  });

  const list = transcript.data ?? [];

  if (transcript.isPending) return <Spinner label={t('common.states.loading')} />;
  if (transcript.isError) return <Alert tone="danger">{errorMessage(transcript.error, t)}</Alert>;
  if (list.length === 0) return <EmptyState title={t('academic.my.transcriptEmpty')} />;

  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{t('academic.school.title')}</th>
            <th scope="col">{t('academic.subject.title')}</th>
            <th scope="col">{t('academic.courseSection.term')}</th>
            <th scope="col">{t('common.labels.status')}</th>
            <th scope="col">{t('academic.enrollment.grade')}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((entry) => (
            <tr key={entry.enrollmentId}>
              <td>{entry.schoolName}</td>
              <td>{entry.subjectName}</td>
              <td dir="ltr">{entry.term}</td>
              <td>{t(`academic.enrollment.status.${entry.status}`)}</td>
              <td dir="ltr">
                {entry.finalGrade !== null ? `${entry.finalGrade} (${entry.letterGrade})` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
